import React, { lazy, Suspense, useState, useEffect } from 'react';
import { Typography, Grid, Card, CardHeader, CardContent, Avatar, Button, Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { menuItems, appHighlightItems } from '../helpers/MenuItems';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { useUser } from '../../contexts/UserContext';
import PageLoading from '../helpers/PageLoading';
import { getCurrentUser } from '../api/services/userService/User';

const lightningKeyframes = `
@keyframes lightning {
  0% {
    filter: brightness(1);
    transform: scale(1);
  }
  25% {
    filter: brightness(1.5) contrast(1.5);
    transform: scale(1.1);
  }
  50% {
    filter: brightness(1.2);
    transform: scale(1);
  }
  75% {
    filter: brightness(1.4);
    transform: scale(1.05);
  }
  100% {
    filter: brightness(1);
    transform: scale(1);
  }
}`;

function getDescription(label) {
  switch (label) {
    case "Dashboard":
      return {
        main: "Operations Dashboard",
        sub: "Monitor servers, agents, and system metrics in real-time."
      };
    case "Agent Studio":
      return {
        main: "AI Agent Builder",
        sub: "Create and deploy intelligent agents using wizards and prebuilt templates."
      };
    case "Apphub":
      return {
        main: "Apphub",
        sub: "Browse preview applications for productivity, analysis, and integration."
      };
    case "Cloud Management":
      return {
        main: "Infrastructure Management",
        sub: "Monitor and manage compute nodes with real-time statistics and provisioning."
      };
    case "Test":
      return {
        main: "API Testing",
        sub: "Test platform APIs and monitor server endpoints in a dedicated environment."
      };
    default:
      return { main: "", sub: "" };
  }
}

export default function Home() {
  const navigate = useNavigate();
  const { keycloak, initialized } = useKeycloak();
  const { user, loading, getUserDisplayName, hasAccess } = useUser();
  const theme = useTheme();

  if (!initialized || loading) {
    return <PageLoading />;
  }

  const filterMenuItems = (items) => {
    return items.filter(item => {
      switch (item.label) {
        case 'Cloud Management':
          return hasAccess('cloudManagement');
        default:
          return true;
      }
    });
  };

  return (
    <Box sx={{ 
      px: { xs: 2, sm: 3, md: 3 },
      pt: 2,
      pb: { xs: 2, sm: 3, md: 3 },
      height: { lg: 'calc(100vh - 64px)' },
      maxHeight: { lg: 'calc(100vh - 64px)' },
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}>
      <style>{lightningKeyframes}</style>
      
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, overflowX: 'hidden' }}>
      <Grid container spacing={{ xs: 2, sm: 2, md: 3 }}>
        {filterMenuItems(menuItems.slice(1)).map((item) => {
          const IconComponent = item.icon;
          return (
            <Grid item xs={12} sm={6} md={6} lg={4} key={item.label}>
              <Card sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: { xs: '180px', sm: '200px', md: '220px' },
                transition: 'box-shadow 0.3s ease-in-out, transform 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: theme.shadows[theme.palette.mode === 'dark' ? 6 : 4],
                  transform: 'translateY(-4px)'
                }
              }}>
                <CardHeader
                  avatar={
                    <Avatar
                      sx={{
                        bgcolor: 'primary.main',
                        width: { xs: 36, sm: 40 },
                        height: { xs: 36, sm: 40 },
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          animation: 'lightning 0.5s ease-in-out',
                          bgcolor: 'primary.light',
                          '& .MuiSvgIcon-root': {
                            color: 'primary.main',
                            transform: 'scale(1.2)',
                          }
                        },
                        '& .MuiSvgIcon-root': {
                          transition: 'all 0.3s ease',
                          fontSize: { xs: '1.2rem', sm: '1.5rem' }
                        }
                      }}
                    >
                      {IconComponent ? <IconComponent /> : null}
                    </Avatar>
                  }
                  title={
                    <Typography variant="h6" sx={{
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      fontWeight: 600
                    }}>
                      {item.label}
                    </Typography>
                  }
                  sx={{ pb: { xs: 1, sm: 2 } }}
                />
                <CardContent sx={{
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  pt: 0,
                  px: { xs: 2, sm: 2 },
                  pb: { xs: 2, sm: 2 }
                }}>
                  <Box>
                    <Typography variant="body1" color="text.primary" sx={{
                      mb: 1,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      lineHeight: { xs: 1.4, sm: 1.5 },
                      fontWeight: 500
                    }}>
                      {getDescription(item.label).main}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{
                      mb: 2,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      lineHeight: { xs: 1.3, sm: 1.4 }
                    }}>
                      {getDescription(item.label).sub}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    endIcon={<ChevronRightIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                    fullWidth
                    size={window.innerWidth < 600 ? "small" : "medium"}
                    sx={{
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      py: { xs: 0.75, sm: 1 }
                    }}
                    onClick={() => {
                      if (item.path.startsWith('http://') || item.path.startsWith('https://')) {
                        window.open(item.path, '_blank');
                      } else {
                        navigate(item.path);
                      }
                    }}
                  >
                    Go to {item.label}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ mt: { xs: 1.5, sm: 2 }, flexShrink: 0 }}>
        <Typography variant="h5" gutterBottom sx={{
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          fontWeight: 600
        }}>
          Platform Highlights
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{
          mb: { xs: 1, sm: 1.5 },
          fontSize: { xs: '0.875rem', sm: '1rem' }
        }}>
          Explore advanced features and specialized tools designed to accelerate your AI development workflow
        </Typography>
        
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} sm={12} md={6}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              minHeight: { xs: '150px', sm: '170px' },
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.15)} 0%, ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.08)} 100%)`,
              color: 'text.primary',
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.25),
              transition: 'box-shadow 0.3s ease-in-out, transform 0.2s ease-in-out',
              '&:hover': {
                boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.18)}`,
                transform: 'translateY(-6px)'
              }
            }}>
              <CardContent sx={{ 
                flexGrow: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'space-between',
                p: { xs: 2, sm: 2.5 }
              }}>
                <Box>
                  <Typography variant="h6" sx={{ 
                    mb: 1.5,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    fontWeight: 700
                  }}>
                    🚀 Agent Studio
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    mb: 2,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    lineHeight: { xs: 1.4, sm: 1.5 },
                    opacity: 0.9
                  }}>
                    Create powerful AI agents without coding. Drag, drop, and deploy intelligent solutions for customer service, automation, and business processes.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  size={window.innerWidth < 600 ? "small" : "medium"}
                  sx={{
                    bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.1 : 0.2),
                    color: 'text.primary',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    py: { xs: 0.75, sm: 1 },
                    '&:hover': {
                      bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.15 : 0.3)
                    }
                  }}
                  onClick={() => navigate('/agent-studio')}
                >
                  Launch Agent Studio
                </Button>

              </CardContent>
            </Card>
          </Grid>
                    
          <Grid item xs={12} sm={12} md={6}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              minHeight: { xs: '150px', sm: '170px' },
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.15)} 0%, ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.08)} 100%)`,
              color: 'text.primary',
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.25),
              transition: 'box-shadow 0.3s ease-in-out, transform 0.2s ease-in-out',
              '&:hover': {
                boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.18)}`,
                transform: 'translateY(-6px)'
              }
            }}>
              <CardContent sx={{ 
                flexGrow: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'space-between',
                p: { xs: 2, sm: 2.5 }
              }}>
                <Box>
                  <Typography variant="h6" sx={{ 
                    mb: 1.5,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    fontWeight: 700
                  }}>
                    🤖 PioAgent
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    mb: 2,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    lineHeight: { xs: 1.4, sm: 1.5 },
                    opacity: 0.9
                  }}>
                    Advanced RAG-powered chatbot with retrieval-augmented generation for accurate, context-aware AI conversations and support.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  size={window.innerWidth < 600 ? "small" : "medium"}
                  sx={{
                    bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.1 : 0.2),
                    color: 'text.primary',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    py: { xs: 0.75, sm: 1 },
                    '&:hover': {
                      bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.15 : 0.3)
                    }
                  }}
                  onClick={() => window.open(process.env.REACT_APP_PIOAGENT_URL || 'https://pioagent.piosphere.ai', '_blank')}
                >
                  Launch PioAgent
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      </Box>

    </Box>
  );
} 